import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    UploadCloud, FileText, ArrowLeft, ArrowRight, Check, Loader2,
    Sparkles, PlayCircle, Film, Wand2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const ADMIN_ROLES = new Set(["ORG_ADMIN", "LD_ADMIN", "CONTENT_CREATOR"]);

// Persistent wizard state — auto-resume across page reloads
const STORAGE_KEY = "onboard-wizard-state-v1";

const AUDIENCES = [
    // target_duration (seconds) maps to target_video_minutes (5–15, 0.5 steps).
    { value: "NEW_JOINER", label: "New joiner", target_duration: 300 },
    { value: "REFRESHER", label: "Refresher", target_duration: 300 },
    { value: "ADVANCED", label: "Advanced", target_duration: 420 },
];

const BUILD_MESSAGES = [
    "Writing the trainer's script…",
    "Filming your trainer…",
    "Adding cinematic clips…",
    "Recording the voiceover…",
    "Finishing touches…",
];

function loadState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
    catch { return {}; }
}
function saveState(s) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}
function clearState() {
    localStorage.removeItem(STORAGE_KEY);
}
// Resume saved progress only on a browser reload of the wizard; any fresh entry starts clean.
let resumeConsumed = false;
function initialState() {
    const navType = (performance.getEntriesByType?.("navigation") || [])[0]?.type;
    const resume = !resumeConsumed && navType === "reload";
    resumeConsumed = true;
    if (resume) return loadState();
    clearState();
    return {};
}

export default function OnboardWizard() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [state, setState] = useState(initialState);
    const step = state.step || 1;

    const setField = useCallback((k, v) => {
        setState((s) => {
            const next = { ...s, [k]: v };
            saveState(next);
            return next;
        });
    }, []);
    const setFields = useCallback((obj, replace = false) => {
        setState((s) => {
            const next = replace ? { ...obj } : { ...s, ...obj };
            saveState(next);
            return next;
        });
    }, []);
    const goto = (n) => setField("step", n);

    if (!user || !ADMIN_ROLES.has(user.role)) {
        return (
            <div
                data-testid="onboard-forbidden"
                className="rounded-lg border border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500"
            >
                Creating a training video is restricted to organisation admins.
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white" data-testid="onboard-wizard">
            <div className="mx-auto max-w-2xl px-6 pt-16 pb-24">
                <div className="mb-10 flex items-center gap-3">
                    <div className="text-xs font-medium uppercase tracking-[0.18em] text-neutral-400">
                        Step {step} of 4
                    </div>
                    <div className="h-[2px] flex-1 bg-neutral-100 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-neutral-900 transition-all duration-500"
                            style={{ width: `${(step / 4) * 100}%` }}
                        />
                    </div>
                </div>

                {step === 1 && (
                    <StepUpload state={state} setField={setField} setFields={setFields} onNext={() => goto(2)} />
                )}
                {step === 2 && (
                    <StepBasics
                        state={state}
                        setField={setField}
                        onBack={() => goto(1)}
                        onNext={() => goto(3)}
                    />
                )}
                {step === 3 && (
                    <StepReview
                        state={state}
                        setField={setField}
                        onBack={() => goto(2)}
                        onNext={() => goto(4)}
                    />
                )}
                {step === 4 && (
                    <StepBuilding state={state} setField={setField} />
                )}

                <button
                    type="button"
                    className="mt-12 text-xs text-neutral-400 hover:text-neutral-700"
                    onClick={() => { if (window.confirm("Start over? Any progress will be lost.")) { clearState(); setState({ step: 1 }); } }}
                    data-testid="wizard-reset"
                >
                    Start over
                </button>
            </div>
        </div>
    );
}

/* Step 1 — Upload */
// Early progress messages rotate every 800ms while extraction is running,
// so the user always sees motion even during the ~5-8s AI reasoning call.
const EARLY_MESSAGES = [
    "Reading page structure…",
    "Finding steps…",
    "Understanding diagrams…",
    "Almost there…",
];

function fmtSize(n) {
    if (!n && n !== 0) return "";
    return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
}

// "Extrusion_Line_Startup_Manual_VIDEO.docx" -> "Extrusion Line Startup Manual"
const TRAILING_TOKENS = /^(video|final|draft|copy|new|latest|rev\d*|r\d+|v\d+(\.\d+)?|\d+)$/i;
function prettyFileTitle(name) {
    const words = (name || "").replace(/\.[^.]+$/, "").replace(/[_\-]+/g, " ").trim().split(/\s+/).filter(Boolean);
    while (words.length > 1 && TRAILING_TOKENS.test(words[words.length - 1])) words.pop();
    return words.map((w) => (w === w.toUpperCase() && w.length <= 4 ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())).join(" ");
}

const POLL_MS = 1500;
const POLL_MAX = 160; // ~4 min

function StepUpload({ state, setField, setFields, onNext }) {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [slowNotice, setSlowNotice] = useState(false);
    const [earlyMsgIdx, setEarlyMsgIdx] = useState(0);
    const [realPct, setRealPct] = useState(0);
    const [realCounter, setRealCounter] = useState("");
    const [stageLabel, setStageLabel] = useState("Starting…");
    const inputRef = useRef(null);
    const slowTimerRef = useRef(null);
    const busyRef = useRef(false);
    const advanceRef = useRef(null);

    const documentReady = state.document_ready;

    useEffect(() => {
        if (!busy) return;
        const msgTimer = setInterval(() => setEarlyMsgIdx((i) => (i + 1) % EARLY_MESSAGES.length), 800);
        return () => clearInterval(msgTimer);
    }, [busy]);
    useEffect(() => () => { clearTimeout(advanceRef.current); clearTimeout(slowTimerRef.current); }, []);

    const pickFile = (f) => {
        if (!f || busyRef.current) return;
        setError("");
        setFile(f);
    };

    const startUpload = async (f = file) => {
        if (!f) return toast.error("Please choose a document first.");
        if (busyRef.current) return;
        busyRef.current = true;
        setBusy(true);
        setError("");
        setRealPct(0);
        setEarlyMsgIdx(0);
        setRealCounter("");
        setStageLabel("Uploading…");
        setSlowNotice(false);
        // A new upload always starts from a clean wizard state.
        setFields({ step: 1 }, true);
        slowTimerRef.current = setTimeout(() => setSlowNotice(true), 60000);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("type", "Work Instruction");
            let data;
            try {
                ({ data } = await api.post("/documents", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        if (e.total) setRealPct(Math.round((e.loaded / e.total) * 30));
                    },
                }));
            } catch (e) {
                throw new Error(`Upload failed: ${formatApiError(e)}`);
            }
            setRealPct(30);
            setStageLabel("Reading your document…");
            const STAGE_MSG = {
                understanding_sections: "Understanding your document…",
                persisting_sections: "Extracting the outline…",
                persisting_steps: "Extracting each step…",
                classifying_images: "Understanding your diagrams…",
                ready: "Almost ready…",
            };
            const STAGE_PCT = {
                understanding_sections: 60, persisting_sections: 70,
                persisting_steps: 80, classifying_images: 90, ready: 95,
            };
            let misses = 0;
            for (let i = 0; ; i++) {
                if (i >= POLL_MAX) {
                    throw new Error("Your document is uploaded but is taking unusually long to read. Retry in a minute, or open it from Documents.");
                }
                await new Promise((r) => setTimeout(r, POLL_MS));
                let wrap;
                try {
                    ({ data: wrap } = await api.get(`/documents/${data.id}`));
                    misses = 0;
                } catch (e) {
                    if (++misses >= 5) throw new Error(`Lost contact while reading the document: ${formatApiError(e)}`);
                    continue;
                }
                const doc = wrap.document || {};
                const cv = wrap.current_version;
                const counts = wrap.counts || {};
                const prog = doc.extraction_progress || {};
                const bits = [];
                if (counts.steps) bits.push(`${counts.steps} steps`);
                if (counts.media) bits.push(`${counts.media} images`);
                setRealCounter(bits.join(" · "));
                if (doc.status === "PARSING" || doc.status === "UPLOADED") {
                    setRealPct((p) => Math.max(p, 40));
                } else if (cv) {
                    setRealPct((p) => Math.max(p, STAGE_PCT[prog.current_stage] || 55));
                    if (STAGE_MSG[prog.current_stage]) setStageLabel(STAGE_MSG[prog.current_stage]);
                }
                if (doc.status === "FAILED") {
                    const pe = doc.parse_error || "";
                    throw new Error(pe && !pe.startsWith("Parser crashed")
                        ? pe
                        : `We couldn't read that document — the reader hit an error${pe ? ` (${pe.slice(0, 160)})` : ""}. Try another file.`);
                }
                const parseOk = doc.status === "VERIFIED" || doc.status === "NEEDS_REVIEW";
                if (parseOk && cv && (counts.steps || 0) > 0) {
                    setRealPct(100);
                    setStageLabel("Ready");
                    setFields({
                        step: 1,
                        document_id: data.id,
                        document_name: f.name,
                        document_size: f.size,
                        document_version_id: cv.id,
                        document_title_guess: doc.parsed_title || prettyFileTitle(f.name),
                        document_step_count: counts.steps || 0,
                        document_image_count: counts.media || 0,
                        document_ready: true,
                    }, true);
                    advanceRef.current = setTimeout(onNext, 1500);
                    break;
                }
                if (parseOk && cv && (doc.extraction_status === "READY" || doc.extraction_status === "FAILED")) {
                    throw new Error("We read the file but couldn't find any steps in it. Make sure the procedure uses numbered steps (1., 2., …) or a numbered list, then try again.");
                }
            }
        } catch (e) {
            setError(e?.message || formatApiError(e));
        } finally {
            clearTimeout(slowTimerRef.current);
            setSlowNotice(false);
            busyRef.current = false;
            setBusy(false);
        }
    };

    const tryAnother = () => {
        setError("");
        setFile(null);
        setFields({ step: 1 }, true);
        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <div className="space-y-8" data-testid="wizard-step-upload">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
                    Let's turn your document into a training video
                </h1>
                <p className="mt-3 text-neutral-500">
                    Drop in a work instruction or SOP. We'll handle the rest.
                </p>
            </div>

            {!documentReady ? (
                <>
                    <div
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer?.files?.[0]; if (f && !busyRef.current) { pickFile(f); startUpload(f); } }}
                        className="rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/40 px-10 py-16 text-center transition-colors hover:border-neutral-300 hover:bg-neutral-50"
                        data-testid="wizard-dropzone"
                    >
                        <UploadCloud className="mx-auto mb-4 text-neutral-400" size={38} strokeWidth={1.4} />
                        {file ? (
                            <div className="text-sm text-neutral-900" data-testid="wizard-file-chosen">
                                <FileText className="inline mr-2 mb-1 text-neutral-400" size={16} />
                                <span data-testid="wizard-file-name">{file.name}</span>
                                <span className="ml-2 text-neutral-500" data-testid="wizard-file-size">{fmtSize(file.size)}</span>
                                {!busy && (
                                    <button
                                        onClick={tryAnother}
                                        className="ml-2 text-neutral-400 hover:text-neutral-700"
                                        data-testid="wizard-clear-file"
                                    ><X size={14} /></button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="text-neutral-900 font-medium">Drag your document here</div>
                                <div className="mt-1 text-sm text-neutral-500">
                                    or{" "}
                                    <button
                                        type="button"
                                        onClick={() => inputRef.current?.click()}
                                        className="text-neutral-900 underline underline-offset-2 hover:text-black"
                                        data-testid="wizard-browse"
                                    >click to browse</button>
                                </div>
                                <div className="mt-6 text-[11px] uppercase tracking-widest text-neutral-400">
                                    DOCX · PDF · PPTX
                                </div>
                            </>
                        )}
                        <input
                            ref={inputRef}
                            type="file"
                            accept=".docx,.pdf,.pptx"
                            hidden
                            onChange={(e) => { const f = e.target.files?.[0]; pickFile(f); if (f) startUpload(f); }}
                            data-testid="wizard-file-input"
                        />
                    </div>

                    {busy && (
                        <div
                            className="rounded-xl border border-neutral-200 bg-white px-4 py-4 space-y-3"
                            data-testid="wizard-upload-progress"
                        >
                            <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2 text-neutral-800 font-medium">
                                    <Loader2 size={14} className="animate-spin text-neutral-500" />
                                    <span data-testid="wizard-stage-label">{stageLabel}</span>
                                </div>
                                <div className="text-xs tabular-nums text-neutral-500" data-testid="wizard-pct">
                                    {Math.round(realPct)}%
                                </div>
                            </div>
                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
                                <div
                                    className="h-full bg-neutral-900 transition-[width] duration-200 ease-out"
                                    style={{ width: `${realPct}%` }}
                                    data-testid="wizard-progress-bar"
                                />
                            </div>
                            <div className="flex items-center justify-between text-xs text-neutral-500">
                                <span key={earlyMsgIdx} className="italic" data-testid="wizard-early-msg">
                                    {realCounter ? "" : EARLY_MESSAGES[earlyMsgIdx]}
                                </span>
                                {realCounter && (
                                    <span data-testid="wizard-real-counter" className="tabular-nums text-neutral-600">
                                        {realCounter} found
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    {slowNotice && (
                        <div className="text-sm text-neutral-500" data-testid="wizard-slow-notice">
                            This one has a lot in it — hang tight.
                        </div>
                    )}
                    {error && !busy && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-800 space-y-3"
                             data-testid="wizard-upload-error">
                            <div data-testid="wizard-upload-error-text">{error}</div>
                            <div className="flex gap-2">
                                {file && (
                                    <Button size="sm" variant="outline" onClick={() => startUpload(file)}
                                            data-testid="wizard-upload-retry">Retry</Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => { tryAnother(); inputRef.current?.click(); }}
                                        data-testid="wizard-upload-try-another">Try another file</Button>
                            </div>
                        </div>
                    )}

                    {!(error && !busy) && (
                    <div className="flex justify-end">
                        <Button
                            size="lg"
                            onClick={() => startUpload(file)}
                            disabled={!file || busy}
                            data-testid="wizard-next-1"
                            className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-8"
                        >
                            {busy ? "Reading…" : "Continue"} <ArrowRight size={16} className="ml-1.5" />
                        </Button>
                    </div>
                    )}
                </>
            ) : (
                <>
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/50 px-6 py-5 text-sm text-emerald-900 flex items-start gap-3"
                         data-testid="wizard-upload-done">
                        <Check size={18} className="mt-0.5" />
                        <div>
                            <div className="font-medium">{state.document_name}
                                <span className="ml-2 font-normal text-emerald-700/70">{fmtSize(state.document_size)}</span>
                            </div>
                            <div className="mt-1 text-emerald-700/80 text-xs" data-testid="wizard-read-summary">
                                Read successfully · {state.document_step_count || 0} steps · {state.document_image_count || 0} images
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <button onClick={tryAnother} className="text-sm text-neutral-500 hover:text-neutral-900"
                                data-testid="wizard-upload-different">Use a different file</button>
                        <Button
                            size="lg"
                            onClick={() => { clearTimeout(advanceRef.current); onNext(); }}
                            data-testid="wizard-continue-1"
                            className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-8"
                        >
                            Continue <ArrowRight size={16} className="ml-1.5" />
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

/* Step 2 — Confirm the basics */
function StepBasics({ state, setField, onBack, onNext }) {
    const [title, setTitle] = useState(state.title || state.document_title_guess || "");
    const [audience, setAudience] = useState(state.audience || "NEW_JOINER");
    const audienceCfg = AUDIENCES.find((a) => a.value === audience);
    const est = audienceCfg?.target_duration || 300;

    const cont = () => {
        if (!title.trim()) return toast.error("Give your video a short title.");
        setField("title", title.trim());
        setField("audience", audience);
        setField("target_duration", est);
        onNext();
    };

    return (
        <div className="space-y-8" data-testid="wizard-step-basics">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
                    A couple of details
                </h1>
                <p className="mt-3 text-neutral-500">
                    We've filled these in for you — tweak if you like.
                </p>
            </div>

            <div className="space-y-6">
                <div>
                    <Label className="text-xs uppercase tracking-widest text-neutral-500">Title</Label>
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-2 h-12 rounded-xl border-neutral-200 text-base"
                        placeholder="e.g. Calibrating the vernier caliper"
                        data-testid="wizard-title-input"
                    />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-neutral-500">Who's it for</Label>
                    <Select value={audience} onValueChange={setAudience}>
                        <SelectTrigger className="mt-2 h-12 rounded-xl border-neutral-200 text-base" data-testid="wizard-audience-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {AUDIENCES.map((a) => (
                                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="rounded-xl bg-neutral-50 px-4 py-3 text-sm text-neutral-600 flex items-center justify-between">
                    <span>Estimated length</span>
                    <span className="font-medium text-neutral-900" data-testid="wizard-duration-est">
                        ~{Math.round(est / 60)} min
                    </span>
                </div>
            </div>

            <div className="flex items-center justify-between pt-4">
                <button
                    onClick={onBack}
                    className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                    data-testid="wizard-back-2"
                >
                    <ArrowLeft size={14} /> Back
                </button>
                <Button
                    size="lg"
                    onClick={cont}
                    data-testid="wizard-next-2"
                    className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-8"
                >
                    Continue <ArrowRight size={16} className="ml-1.5" />
                </Button>
            </div>
        </div>
    );
}

/* Step 3 — Review extracted steps */
function StepReview({ state, setField, onBack, onNext }) {
    const [steps, setSteps] = useState(state.steps_cache || []);
    const [loading, setLoading] = useState(!state.steps_cache);
    const [busy, setBusy] = useState(false);
    const [editingIdx, setEditingIdx] = useState(null);
    const [editText, setEditText] = useState("");

    useEffect(() => {
        if (state.steps_cache?.length) return;
        (async () => {
            try {
                // Steps live under the document's current version — hit the
                // real endpoint (List[ProcedureStep]) not the document row itself.
                let vid = state.document_version_id;
                if (!vid) {
                    const { data: wrap } = await api.get(`/documents/${state.document_id}`);
                    vid = wrap.current_version?.id;
                    setField("document_version_id", vid);
                }
                const { data: raw } = await api.get(
                    `/documents/${state.document_id}/versions/${vid}/steps`,
                );
                const list = (raw || []).map((s, i) => ({
                    idx: i,
                    id: s.id,
                    number: s.step_number || String(i + 1),
                    title: s.title
                        || (s.learner_friendly_instruction || s.validated_instruction || s.original_instruction || `Step ${i + 1}`)
                            .replace(/^\s*(?:step\s*)?\d{1,3}\s*[.):-]?\s*/i, "").split(/[.!?](?:\s|$)/)[0].slice(0, 80),
                    summary: (s.learner_friendly_instruction || s.validated_instruction || s.original_instruction || "").slice(0, 160),
                    included: true,
                    thumbnail: s.thumbnail_url || s.image_url || null,
                }));
                setSteps(list);
                setField("steps_cache", list);
            } catch (e) {
                setError(`Couldn't load the steps: ${formatApiError(e)}`);
            } finally {
                setLoading(false);
            }
        })();
    }, [state.document_id, state.document_version_id, state.steps_cache, setField]);

    const saveEdit = () => {
        setSteps((s) => {
            const next = s.map((x) => x.idx === editingIdx ? { ...x, summary: editText, edited: true } : x);
            setField("steps_cache", next);
            return next;
        });
        setEditingIdx(null);
    };
    const removeStep = (i) => {
        setSteps((s) => {
            const next = s.map((x) => x.idx === i ? { ...x, included: false } : x);
            setField("steps_cache", next);
            return next;
        });
    };
    const restoreStep = (i) => {
        setSteps((s) => {
            const next = s.map((x) => x.idx === i ? { ...x, included: true } : x);
            setField("steps_cache", next);
            return next;
        });
    };

    const [error, setError] = useState("");
    const createModule = async () => {
        if (!steps.some((s) => s.included)) return toast.error("Include at least one step.");
        setBusy(true);
        setError("");
        let stage = "approve";
        try {
            // The wizard's keep/remove/edit pass is the document review.
            const edits = {};
            steps.forEach((s) => { if (s.included && s.edited && s.id) edits[s.id] = s.summary; });
            await api.post(`/documents/${state.document_id}/reviews/wizard-approve`, {
                include_step_ids: steps.filter((s) => s.included && s.id).map((s) => s.id),
                exclude_step_ids: steps.filter((s) => !s.included && s.id).map((s) => s.id),
                edits,
            });
            let moduleId = state.module_id;
            if (!moduleId) {
                stage = "create";
                const audLabel = (AUDIENCES.find((a) => a.value === state.audience) || {}).label || "";
                const { data } = await api.post("/modules", {
                    document_id: state.document_id,
                    name: state.title || state.document_title_guess,
                    target_video_minutes: Math.min(15, Math.max(5, Math.round(((state.target_duration || 300) / 60) * 2) / 2)),
                    description: audLabel ? `Audience: ${audLabel}` : undefined,
                });
                moduleId = data.id;
                setField("module_id", moduleId);
            }
            stage = "generate";
            const { data: gen } = await api.post(`/modules/${moduleId}/generate`);
            setField("generate_job_id", gen?.job_id || null);
            onNext();
        } catch (e) {
            const what = { approve: "Couldn't confirm the steps", create: "Couldn't create the video project", generate: "Couldn't start writing the script" }[stage];
            setError(`${what}: ${formatApiError(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-8" data-testid="wizard-step-review">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
                    Here's what we found
                </h1>
                <p className="mt-3 text-neutral-500">
                    Review the steps we'll turn into scenes. Tweak, remove, or roll on.
                </p>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-500 flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading steps…
                </div>
            ) : (
                <div className="space-y-3">
                    {steps.map((s) => (
                        <div
                            key={s.idx}
                            data-testid={`wizard-step-card-${s.number}`}
                            className={`rounded-2xl border px-5 py-4 transition-all ${
                                s.included
                                    ? "border-neutral-200 bg-white shadow-sm"
                                    : "border-neutral-100 bg-neutral-50/50 opacity-60"
                            }`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="w-16 aspect-video rounded-lg bg-neutral-100 shrink-0 flex items-center justify-center overflow-hidden">
                                    {s.thumbnail ? (
                                        <img src={s.thumbnail} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                        <FileText size={16} className="text-neutral-400" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[11px] uppercase tracking-widest text-neutral-400 mb-0.5">
                                        Step {s.number}
                                    </div>
                                    <div className="text-sm font-medium text-neutral-900 truncate">{s.title}</div>
                                    {editingIdx === s.idx ? (
                                        <div className="mt-2">
                                            <Textarea
                                                value={editText}
                                                onChange={(e) => setEditText(e.target.value)}
                                                rows={2}
                                                className="text-sm rounded-lg border-neutral-200"
                                                data-testid={`wizard-step-edit-${s.number}`}
                                            />
                                            <div className="mt-2 flex gap-2">
                                                <Button size="sm" onClick={saveEdit} className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800">Save</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditingIdx(null)}>Cancel</Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-sm text-neutral-500 mt-1 line-clamp-2">{s.summary || "—"}</div>
                                    )}
                                </div>
                                {editingIdx !== s.idx && s.included && (
                                    <div className="flex gap-1 shrink-0">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => { setEditingIdx(s.idx); setEditText(s.summary); }}
                                            data-testid={`wizard-step-edit-btn-${s.number}`}
                                            className="text-neutral-500 hover:text-neutral-900"
                                        >Edit text</Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => removeStep(s.idx)}
                                            data-testid={`wizard-step-remove-${s.number}`}
                                            className="text-neutral-400 hover:text-neutral-900"
                                        >Remove</Button>
                                    </div>
                                )}
                                {!s.included && (
                                    <Button size="sm" variant="ghost" onClick={() => restoreStep(s.idx)}>
                                        Restore
                                    </Button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start justify-between gap-3"
                     data-testid="wizard-create-error">
                    <span>{error}</span>
                    <Button size="sm" variant="outline" onClick={createModule} disabled={busy}
                            data-testid="wizard-create-retry">Retry</Button>
                </div>
            )}
            {!loading && steps.length === 0 && (
                <div className="text-sm text-red-700" data-testid="wizard-no-steps">
                    No steps were found in this document. Go back and try another file.
                </div>
            )}

            <div className="flex items-center justify-between pt-4">
                <button
                    onClick={onBack}
                    className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1"
                    data-testid="wizard-back-3"
                >
                    <ArrowLeft size={14} /> Back
                </button>
                <Button
                    size="lg"
                    onClick={createModule}
                    disabled={busy || loading}
                    data-testid="wizard-create-video"
                    className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-8"
                >
                    <Wand2 size={16} className="mr-2" />
                    {busy ? "Creating…" : "Create my video"}
                </Button>
            </div>
        </div>
    );
}

/* Step 4 — Building your video */
function StepBuilding({ state, setField }) {
    const nav = useNavigate();
    const [msgIdx, setMsgIdx] = useState(0);
    const [ready, setReady] = useState(false);
    const [failMsg, setFailMsg] = useState("");
    const [attempt, setAttempt] = useState(0);
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        const t = setInterval(() => setMsgIdx((i) => (i + 1) % BUILD_MESSAGES.length), 4500);
        return () => clearInterval(t);
    }, []);

    const goReview = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        nav(`/videos/${state.module_id}?step=script`);
    }, [nav, state.module_id]);

    useEffect(() => {
        if (!state.module_id) { setFailMsg("The video project wasn't created. Go back and try again."); return; }
        let cancelled = false;
        let timer;
        (async () => {
            for (let i = 0; i < 300; i++) {  // ~15 min max
                if (cancelled) return;
                try {
                    const { data } = await api.get(`/modules/${state.module_id}`);
                    const m = data.module || data;
                    if (["STORYBOARD_READY", "READY_FOR_REVIEW", "APPROVED", "PUBLISHED"].includes(m.status)) {
                        setReady(true);
                        timer = setTimeout(goReview, 1200);
                        return;
                    }
                    if (m.status === "FAILED") {
                        let reason = "";
                        if (state.generate_job_id) {
                            try { reason = (await api.get(`/jobs/${state.generate_job_id}`)).data?.error || ""; } catch { /* ignore */ }
                        }
                        if (!cancelled) setFailMsg(`Writing the script failed${reason ? `: ${reason.slice(0, 240)}` : "."}`);
                        return;
                    }
                } catch { /* transient */ }
                await new Promise((r) => setTimeout(r, 3000));
            }
            if (!cancelled) setFailMsg("This is taking much longer than usual. You can retry, or open the project from Videos later.");
        })();
        return () => { cancelled = true; clearTimeout(timer); };
    }, [state.module_id, state.generate_job_id, attempt, goReview]);

    const retry = async () => {
        setRetrying(true);
        try {
            const { data } = await api.post(`/modules/${state.module_id}/generate`);
            setField("generate_job_id", data?.job_id || null);
            setFailMsg("");
            setAttempt((a) => a + 1);
        } catch (e) {
            setFailMsg(`Couldn't restart: ${formatApiError(e)}`);
        } finally {
            setRetrying(false);
        }
    };

    return (
        <div className="space-y-10 text-center py-8" data-testid="wizard-step-building">
            <div>
                <h1 className="text-3xl font-semibold tracking-tight text-neutral-900">
                    Writing your script &amp; scenes
                </h1>
                <p className="mt-3 text-neutral-500">
                    Sit tight — this usually takes a minute or two.
                </p>
            </div>

            <div className="mx-auto max-w-md">
                <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-neutral-50">
                    {ready
                        ? <Check size={40} className="text-emerald-600" />
                        : failMsg
                            ? <Sparkles size={36} className="text-amber-600" />
                            : <Loader2 size={40} className="animate-spin text-neutral-700" />}
                </div>
                {!ready && !failMsg && (
                    <div className="mt-8 text-neutral-700" data-testid="wizard-build-message" key={msgIdx}>
                        {BUILD_MESSAGES[msgIdx]}
                    </div>
                )}
                {ready && (
                    <div className="mt-8 space-y-4" data-testid="wizard-build-done">
                        <div className="text-neutral-700">Your script and scenes are ready — opening them now…</div>
                        <Button size="lg" onClick={goReview} data-testid="wizard-review"
                                className="rounded-full bg-neutral-900 text-white hover:bg-neutral-800 px-8">
                            <Check size={18} className="mr-2" /> Review script &amp; scenes
                        </Button>
                    </div>
                )}
                {failMsg && (
                    <div className="mt-8 space-y-3 text-sm text-neutral-700" data-testid="wizard-soft-fail">
                        <p data-testid="wizard-build-error">{failMsg}</p>
                        <div className="flex justify-center gap-2">
                            {state.module_id && (
                                <Button onClick={retry} disabled={retrying} data-testid="wizard-build-retry">
                                    {retrying ? "Retrying…" : "Retry"}
                                </Button>
                            )}
                            <Button variant="outline" onClick={() => nav("/videos")}>Back to my videos</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
